import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { MapPin, Star, Heart, Users, CalendarCheck, Dumbbell } from "lucide-react";
import { photoSrc } from "@/components/RoomPhotosUploader";
import ShareButton from "@/components/ShareButton";
import CoachAvatar from "@/components/CoachAvatar";
import ReviewCard from "@/components/ReviewCard";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function RoomShowcase() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [hero, setHero] = useState(null);
  useEffect(() => { api.get(`/rooms/${id}/showcase`).then((r) => { setData(r.data); setHero(r.data.room.photo_url); }).catch(() => setData(false)); }, [id]);

  if (data === false) return <div><Navbar /><div className="p-10 text-slate-500">Salle introuvable.</div></div>;
  if (!data) return <div><Navbar /><div className="p-10 text-slate-500">Chargement…</div></div>;
  const { room, reviews, slots, stats, coaches, rating_avg } = data;
  const photos = [...new Set([room.photo_url, ...(room.photos || [])].filter(Boolean))];

  const weekStart = new Date(); weekStart.setHours(0, 0, 0, 0); weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
  const days = [...Array(7)].map((_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const nextWeek = [...Array(7)].map((_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + 7 + i); return d; });

  const dayCol = (d, i) => {
    const ds = slots.filter((s) => new Date(s.start).toDateString() === d.toDateString());
    return (
      <div key={d.toISOString()} className={`rounded-xl border p-2 min-h-[90px] ${d.toDateString() === new Date().toDateString() ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
        <div className="text-[11px] font-semibold text-slate-600">{DAYS[i]} {d.getDate()}</div>
        <div className="mt-1 space-y-1">
          {ds.map((s) => <div key={s.id} className={`text-[10px] px-1.5 py-0.5 rounded-md border ${s.status === "available" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-200 line-through"}`}>{new Date(s.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>)}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sc-fade-in" data-testid="room-showcase">
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <div className="relative rounded-3xl overflow-hidden bg-slate-200 aspect-[16/10]">
              {hero && <img src={photoSrc(hero)} alt={room.name} className="w-full h-full object-cover" data-testid="showcase-hero" />}
              <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-slate-900/80 to-transparent text-white">
                <div className="text-xs uppercase tracking-widest font-bold text-emerald-300">Espace partenaire TymCoach</div>
                <h1 className="text-3xl sm:text-4xl font-extrabold" data-testid="showcase-name">{room.name}</h1>
                <div className="flex items-center gap-2 text-sm mt-1"><MapPin className="w-4 h-4" />{room.address ? `${room.address}, ` : ""}{room.neighborhood} · {room.city}</div>
              </div>
            </div>
            {photos.length > 1 && (
              <div className="mt-2 grid grid-cols-5 gap-2" data-testid="showcase-gallery">
                {photos.slice(0, 10).map((p) => <button key={p} onClick={() => setHero(p)} className={`aspect-square rounded-xl overflow-hidden border-2 ${hero === p ? "border-emerald-500" : "border-transparent"}`}><img src={photoSrc(p)} alt="" className="w-full h-full object-cover" /></button>)}
              </div>
            )}
          </div>
          <div className="lg:col-span-2 space-y-3">
            <div className="sc-card">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-3xl font-extrabold text-slate-900">{room.price_per_hour} €<span className="text-base font-semibold text-slate-500">/h</span></div>
                  <div className="text-xs text-slate-500">Coopération {room.cooperation_level === "open" ? "ouverte" : room.cooperation_level === "moderate" ? "modérée" : "stricte"}</div>
                </div>
                {rating_avg && <div className="flex items-center gap-1 text-orange-500 font-bold" data-testid="showcase-rating"><Star className="w-5 h-5 fill-current" /> {rating_avg} <span className="text-xs text-slate-500 font-normal">({reviews.length})</span></div>}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                {[[<CalendarCheck className="w-4 h-4 mx-auto text-emerald-600" />, stats.free_slots, "créneaux libres"], [<Users className="w-4 h-4 mx-auto text-sky-600" />, stats.coaches, "coachs partenaires"], [<Heart className="w-4 h-4 mx-auto text-rose-500" />, stats.favorites, "favoris"]].map(([ic, v, l], i) => (
                  <div key={i} className="rounded-xl bg-slate-50 p-2">{ic}<div className="font-bold text-slate-900 text-lg">{v}</div><div className="text-[10px] text-slate-500">{l}</div></div>
                ))}
              </div>
              <div className="mt-2 text-center text-xs text-slate-500">{stats.confirmed_sessions} séances déjà organisées ici</div>
              <Link to={`/salles/${room.id}`} data-testid="showcase-book" className="mt-4 w-full sc-btn sc-btn-primary justify-center">Voir les créneaux et réserver</Link>
              <Link to="/inscription" data-testid="showcase-join" className="mt-2 w-full sc-btn sc-btn-outline justify-center text-sm">Je suis coach, je veux m'entraîner ici</Link>
              <div className="mt-3 flex justify-center"><ShareButton testId="showcase-share" path={`/vitrine/${room.id}`} text={`Découvre ${room.name} (${room.city}) sur TymCoach — créneaux dès ${room.price_per_hour}€/h :`} /></div>
            </div>
            <div className="sc-card">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1"><Dumbbell className="w-3.5 h-3.5" /> Disciplines & équipements</div>
              <div className="mt-2 flex flex-wrap gap-1.5">{(room.disciplines || []).map((d) => <span key={d} className="text-xs px-2.5 py-1 rounded-full bg-orange-500/10 text-orange-700 border border-orange-500/20">{d}</span>)}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">{(room.equipments || []).map((e) => <span key={e} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{e}</span>)}</div>
            </div>
          </div>
        </div>

        {room.description && <p className="mt-8 text-slate-700 max-w-3xl">{room.description}</p>}

        <h2 className="mt-10 text-2xl font-bold text-slate-900">Planning en direct</h2>
        <p className="text-sm text-slate-600 mt-1">Créneaux des deux prochaines semaines — <span className="text-emerald-700 font-semibold">vert = libre</span>, barré = réservé.</p>
        <div className="mt-3 grid grid-cols-7 gap-1.5" data-testid="showcase-planning">{days.map(dayCol)}</div>
        <div className="mt-1.5 grid grid-cols-7 gap-1.5">{nextWeek.map(dayCol)}</div>

        {coaches.length > 0 && (
          <>
            <h2 className="mt-10 text-2xl font-bold text-slate-900">Coachs partenaires</h2>
            <div className="mt-3 flex flex-wrap gap-3" data-testid="showcase-coaches">
              {coaches.map((c) => <Link key={c.id} to={`/coachs/${c.id}`} className="sc-card flex items-center gap-3 hover:ring-2 hover:ring-orange-300"><CoachAvatar coach={c} size="w-10 h-10" textSize="text-sm" /><div><div className="font-semibold text-slate-900">{c.name}</div><div className="text-xs text-slate-500">{(c.specialties || []).slice(0, 2).join(" · ")}</div></div></Link>)}
            </div>
          </>
        )}

        <h2 className="mt-10 text-2xl font-bold text-slate-900">Avis</h2>
        <div className="mt-3 grid md:grid-cols-2 gap-3" data-testid="showcase-reviews">
          {reviews.map((r) => <ReviewCard key={r.id} review={r} canReply={false} />)}
          {reviews.length === 0 && <div className="text-sm text-slate-500">Pas encore d'avis.</div>}
        </div>
      </div>
      <Footer />
    </div>
  );
}
